import React from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  ChipProps,
  FormControl,
  FormHelperText,
  FormLabel,
  formLabelClasses,
  ListItem,
  Stack,
} from '@mui/material';
import type { FieldInputProps, FieldMetaState } from 'react-final-form';

import { SxProp } from '@hrlogics/react-helper-types';

import type { SelectFieldOption } from 'src/types/components';
import { getFieldError, getFieldWarning, hasFieldError } from 'src/utils/finalForm';

export type SelectChipsFieldProps = {
  input: FieldInputProps<string[]>;
  meta: FieldMetaState<string[]>;
  options: SelectFieldOption[];
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  warning?: string;
  showErrorOnlyTouched?: boolean;
  showErrorWhenDisabled?: boolean;
  size?: ChipProps['size'];
};

export const SelectChipsField: React.FC<SelectChipsFieldProps> = ({
  input,
  meta,
  options,
  label,
  disabled = false,
  required = false,
  warning,
  showErrorOnlyTouched = true,
  showErrorWhenDisabled = true,
  size = 'medium',
}) => {
  const { value = [], onBlur, onChange } = input;

  const labelId = React.useId();
  const helperTextId = React.useId();

  const error = hasFieldError(meta, showErrorOnlyTouched);
  const showError = showErrorWhenDisabled || !disabled;
  const helperText = showError
    ? getFieldError(meta, showErrorOnlyTouched) || getFieldWarning(value, meta, warning, disabled)
    : '';

  const handleChipClick = React.useCallback(
    (optionValue: string) => {
      if (disabled) return;

      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(optionValue);

      const newValues = isSelected
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];

      onChange(newValues);
    },
    [disabled, value, onChange],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, optionValue: string) => {
      if (event.key === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        handleChipClick(optionValue);
      }
    },
    [handleChipClick],
  );

  const isSelected = React.useCallback(
    (optionValue: string) => {
      const currentValues = Array.isArray(value) ? value : [];
      return currentValues.includes(optionValue);
    },
    [value],
  );

  return (
    <FormControl
      error={showError ? error : false}
      required={required}
      disabled={disabled}
      fullWidth
      onBlur={onBlur}
    >
      <Box sx={containerStyle}>
        {label && (
          <FormLabel id={labelId} sx={labelStyle(error && showError)}>
            {label}
          </FormLabel>
        )}

        <Stack
          component="ul"
          direction="row"
          flexWrap="wrap"
          role="group"
          spacing={1}
          gap={1}
          aria-labelledby={label ? labelId : undefined}
          aria-required={required}
          aria-invalid={showError ? error : false}
          aria-describedby={helperText ? helperTextId : undefined}
          sx={stackStyle}
        >
          {options.map(option => {
            const selected = isSelected(option.value);
            return (
              <ListItem key={option.value} sx={stackListItemStyle}>
                <Chip
                  label={option.label}
                  size={size}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  disabled={disabled}
                  onClick={() => handleChipClick(option.value)}
                  onDelete={() => handleChipClick(option.value)}
                  onKeyDown={e => handleKeyDown(e, option.value)}
                  clickable
                  deleteIcon={selected ? <CloseIcon /> : <React.Fragment />}
                  tabIndex={disabled ? -1 : 0}
                  role="checkbox"
                  aria-checked={selected}
                  aria-label={option.label}
                  sx={chipStyle(disabled)}
                />
              </ListItem>
            );
          })}
        </Stack>
      </Box>

      {helperText && (
        <FormHelperText id={helperTextId} error={showError ? error : false}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const labelStyle = (isError: boolean): SxProp => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  flexShrink: 0,
  alignSelf: 'center',
  [`&.${formLabelClasses.focused}`]: {
    color: isError ? 'error.main' : 'primary.main',
  },
});

const containerStyle: SxProp = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 2,
};

const stackStyle: SxProp = {
  listStyle: 'none',
  m: 0,
  p: 0,
  flex: 1,
};

const stackListItemStyle: SxProp = {
  display: 'contents',
};

const chipStyle = (disabled: boolean): SxProp => ({
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
  },
});
